import { useState } from "react";
import { PERIOD_PILLS, type PeriodKey } from "@easyshop/shared";
import { Dropdown, cx } from "@/components/ui";
import { IosSegmented } from "@/components/mobile/MobileUi";
import { ymd } from "@/components/planning/utils";
import { usePeriod, usePeriodUrlSync } from "@/hooks/usePeriod";
import {
  SHORTCUTS,
  addDays,
  canShiftNext,
  cmpChoices,
  cmpRange,
  compareLabel,
  isShortcut,
  periodLabel,
  rangeLabel,
  selRange,
  shiftSel,
  startOfWeek,
  type PeriodMode,
  type PeriodSel,
} from "@/lib/period-nav";

const PERIOD_OPTIONS: { key: PeriodMode; label: string }[] = [
  ...(Object.keys(PERIOD_PILLS) as PeriodKey[]).map((key) => ({ key, label: PERIOD_PILLS[key] })),
  { key: "perso", label: "Période…" },
];

const WEEKDAYS = ["L", "M", "M", "J", "V", "S", "D"];

export function CalendarIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 6h16v14H4zM4 10h16M8 3v5M16 3v5" />
    </svg>
  );
}

/**
 * Calendrier mensuel (lundi en premier) : jours futurs grisés, aujourd'hui
 * cerclé, période sélectionnée en vert. `pending` : début d'une plage en cours de choix.
 */
export function MonthCalendar({
  sel,
  onPick,
  pending = null,
  cell = 36,
}: {
  sel: PeriodSel;
  onPick: (day: Date) => void;
  pending?: Date | null;
  cell?: number;
}) {
  const r = selRange(sel);
  const [month, setMonth] = useState(() => new Date(r.to.getFullYear(), r.to.getMonth(), 1));
  const today = new Date();
  const todayKey = ymd(today);
  const start = startOfWeek(month);
  const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const from = pending ? ymd(pending) : ymd(r.from);
  const to = pending ? ymd(pending) : ymd(r.to);
  const canNext = new Date(month.getFullYear(), month.getMonth() + 1, 1) <= today;
  const arrow = "flex h-8 w-8 items-center justify-center rounded-full text-[20px] leading-none text-accent hover:bg-ios-fill disabled:opacity-30 disabled:hover:bg-transparent";
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <button type="button" aria-label="Mois précédent" className={arrow} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>
          ‹
        </button>
        <span className="text-[15px] font-semibold first-letter:uppercase">{month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</span>
        <button type="button" aria-label="Mois suivant" disabled={!canNext} className={arrow} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 text-center">
        {WEEKDAYS.map((d, i) => (
          <span key={i} className="pb-1 text-[11px] font-medium text-muted">
            {d}
          </span>
        ))}
        {days.map((d) => {
          const key = ymd(d);
          const future = key > todayKey;
          const out = d.getMonth() !== month.getMonth();
          const edge = key === from || key === to;
          const inside = key > from && key < to;
          return (
            <button
              key={key}
              type="button"
              disabled={future}
              onClick={() => onPick(d)}
              style={{ height: cell }}
              className={cx("flex items-center justify-center", inside && "bg-[oklch(0.4_0.08_155/0.1)]")}
            >
              <span
                style={{ width: cell - 4, height: cell - 4 }}
                className={cx(
                  "flex items-center justify-center rounded-full text-[14px] tabular",
                  edge ? "bg-accent font-semibold text-white" : key === todayKey ? "font-semibold text-accent ring-1 ring-accent" : "",
                  !edge && (future ? "text-[oklch(0.82_0.006_150)]" : out ? "text-faint" : "text-ink hover:bg-ios-fill")
                )}
              >
                {d.getDate()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Choix d'un jour dans le calendrier selon le mode (jour, semaine/mois/année, plage en 2 clics). */
export function usePickDay(onDone: () => void) {
  const s = usePeriod();
  const [pending, setPending] = useState<Date | null>(null);
  const pick = (day: Date) => {
    if (s.period !== "perso") {
      s.apply({ date: ymd(day) });
      onDone();
      return;
    }
    if (!pending) {
      setPending(day);
      return;
    }
    const [a, b] = day < pending ? [day, pending] : [pending, day];
    s.apply({ period: "perso", range: { from: ymd(a), to: ymd(b) } });
    setPending(null);
    onDone();
  };
  return { pending, pick, reset: () => setPending(null) };
}

const HINT: Record<PeriodMode, string> = {
  jour: "Choisis un jour.",
  semaine: "Choisis un jour : sa semaine s'affiche.",
  mois: "Choisis un jour : son mois s'affiche.",
  annee: "Choisis un jour : son année s'affiche.",
  perso: "Choisis le début, puis la fin.",
};

function DatePopover({ close }: { close: () => void }) {
  const s = usePeriod();
  const { pending, pick } = usePickDay(close);
  return (
    <div className="grid grid-cols-[176px_1fr]">
      <div className="flex flex-col gap-[2px] border-r-[0.5px] border-ios-sep pr-2">
        {SHORTCUTS.map((sc) => (
          <button
            key={sc.label}
            type="button"
            onClick={() => {
              s.apply(sc.sel());
              close();
            }}
            className={cx(
              "rounded-[8px] px-[10px] py-2 text-left text-[14px]",
              isShortcut(s, sc) ? "bg-[oklch(0.4_0.08_155/0.12)] font-semibold text-accent" : "hover:bg-ios-fill"
            )}
          >
            {sc.label}
          </button>
        ))}
      </div>
      <div className="pl-4 pr-1">
        <MonthCalendar sel={s} onPick={pick} pending={pending} />
        <div className="mt-2 text-[12px] text-muted">{pending ? "Choisis la fin." : HINT[s.period]}</div>
      </div>
    </div>
  );
}

/** Petit trait pointillé de la courbe de comparaison. */
export function DashedLine({ className }: { className?: string }) {
  return (
    <svg width="18" height="4" viewBox="0 0 18 4" className={className} aria-hidden>
      <path d="M1 2h16" stroke="currentColor" strokeWidth="1.6" strokeDasharray="3 3" strokeLinecap="round" />
    </svg>
  );
}

function ComparePopover({ close }: { close: () => void }) {
  const s = usePeriod();
  return (
    <div>
      <div className="px-2 pb-2 pt-1 text-[12.5px] leading-[1.4] text-muted">Les écarts (▲ ▼) et la courbe en pointillés se calculent par rapport à :</div>
      {cmpChoices(s).map((c) => {
        const active = s.cmp === c.key;
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => {
              s.setCmp(c.key);
              close();
            }}
            className={cx("flex w-full items-center gap-2 rounded-[10px] px-[10px] py-2 text-left", active ? "bg-accent-soft" : "hover:bg-ios-fill")}
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-semibold">{c.title}</span>
              <span className="block text-[12.5px] text-muted">{rangeLabel(s.period, cmpRange(s, c.key))}</span>
            </span>
            {active && <span className="text-[16px] text-accent">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Barre de période (PC), sur sa propre ligne sous l'en-tête : Jour … Année /
 * Période…, ‹ date › avec calendrier, et « comparé à » à droite.
 */
export function PeriodBar() {
  usePeriodUrlSync();
  const s = usePeriod();
  const choice = cmpChoices(s).find((c) => c.key === s.cmp) ?? cmpChoices(s)[0];
  const arrow = "flex h-10 w-9 items-center justify-center text-[20px] leading-none text-accent disabled:opacity-30";
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="w-[352px] max-w-full">
        <IosSegmented options={PERIOD_OPTIONS} value={s.period} onChange={s.setPeriod} />
      </div>
      <Dropdown
        align="left"
        width={520}
        trigger={(open, toggle) => (
          <div className="flex items-center rounded-[10px] bg-white">
            <button type="button" aria-label="Période précédente" className={arrow} onClick={() => s.apply(shiftSel(s, -1))}>
              ‹
            </button>
            <button
              type="button"
              onClick={toggle}
              aria-expanded={open}
              className={cx("flex h-8 items-center gap-2 rounded-[8px] px-3 text-[14px] font-semibold", open && "bg-ios-fill")}
            >
              <span className="text-accent">
                <CalendarIcon />
              </span>
              {periodLabel(s)}
            </button>
            <button type="button" aria-label="Période suivante" disabled={!canShiftNext(s)} className={arrow} onClick={() => s.apply(shiftSel(s, 1))}>
              ›
            </button>
          </div>
        )}
      >
        {(close) => <DatePopover close={close} />}
      </Dropdown>
      <div className="flex-1" />
      <div className="flex items-center gap-3">
        <span className="text-[13px] text-muted">comparé à</span>
        <Dropdown
          width={330}
          trigger={(_, toggle) => (
            <button type="button" onClick={toggle} className="flex items-center gap-[10px] rounded-[10px] bg-white py-[5px] pl-3 pr-[10px] text-left">
              <DashedLine className="text-muted" />
              <span className="leading-tight">
                <span className="block text-[14px] font-semibold">{compareLabel(s)}</span>
                <span className="block text-[11px] text-muted">{choice.title}</span>
              </span>
              <span aria-hidden className="text-[10px] text-muted">
                ▾
              </span>
            </button>
          )}
        >
          {(close) => <ComparePopover close={close} />}
        </Dropdown>
      </div>
    </div>
  );
}

/** Légende de la courbe CA : « ━ période  ┅ comparé à … ». */
export function ChartLegend() {
  const s = usePeriod();
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px]">
      <span className="flex items-center gap-[6px] font-semibold text-accent">
        <span className="inline-block h-[2px] w-[14px] bg-accent" />
        {periodLabel(s)}
      </span>
      <span className="flex items-center gap-[6px] text-muted">
        <DashedLine />
        comparé à {compareLabel(s)}
      </span>
    </div>
  );
}
